import { isListingAvailable } from '@paction-2.0/util/listings/availability';

import type { ListingDetail, ListingParentDog } from './listing.api';

// ─────────────────────────────────────────────────────────────────────────────
// Formatting utilities
// ─────────────────────────────────────────────────────────────────────────────

export const formatPrice = (price: number | null): string => {
  if (price === null) return 'Contact for price';
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price);
};

export const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'N/A';
  try {
    return new Intl.DateTimeFormat('en-CA', {
      dateStyle: 'medium'
    }).format(new Date(dateString));
  } catch {
    return 'N/A';
  }
};

export const formatSex = (sex: string | null): string => {
  if (!sex) return '';
  const normalized = sex.toLowerCase();
  if (normalized === 'male') return 'Male';
  if (normalized === 'female') return 'Female';
  return sex;
};

export const formatAvailability = (
  isAvailableNow: boolean | null,
  availableDate: string | null
): string => {
  if (isListingAvailable(isAvailableNow, availableDate)) return 'Available Now';
  if (availableDate) return `Available ${formatDate(availableDate)}`;
  return 'Contact for availability';
};

// ─────────────────────────────────────────────────────────────────────────────
// Media extraction
// ─────────────────────────────────────────────────────────────────────────────

export type MediaItem = {
  src: string;
  alt?: string;
  type?: 'image' | 'video';
};

const extractMediaFromRaw = (
  raw: Array<Record<string, unknown>> | null
): MediaItem[] => {
  if (!raw || !Array.isArray(raw)) return [];
  const result: MediaItem[] = [];
  for (const item of raw) {
    const src = (item.src as string) || (item.url as string);
    if (!src || typeof src !== 'string') continue;
    const mediaItem: MediaItem = {
      src,
      type: (item.type as 'image' | 'video') || 'image'
    };
    if (item.alt && typeof item.alt === 'string') {
      mediaItem.alt = item.alt;
    }
    result.push(mediaItem);
  }
  return result;
};

export const extractFirstImageUrl = (listing: ListingDetail): string | null => {
  const pupMedia = listing.pup?.media;
  if (pupMedia && Array.isArray(pupMedia)) {
    for (const item of pupMedia) {
      const src =
        (item.src as string | undefined) || (item.url as string | undefined);
      if (src && typeof src === 'string') return src;
    }
  }
  return null;
};

export const extractAllMedia = (listing: ListingDetail): MediaItem[] => {
  const media: MediaItem[] = [];

  // Pup media
  if (listing.pup?.media) {
    media.push(...extractMediaFromRaw(listing.pup.media));
  }

  // Mother media
  if (listing.mother?.gallery) {
    media.push(...extractMediaFromRaw(listing.mother.gallery));
  }

  // Father media
  if (listing.father?.gallery) {
    media.push(...extractMediaFromRaw(listing.father.gallery));
  }

  return media;
};

// ─────────────────────────────────────────────────────────────────────────────
// Parent dog presentation
// ─────────────────────────────────────────────────────────────────────────────

export type HealthScreeningItem = {
  test: string;
  date: string | null;
  result: string | null;
};

export type ParentDogPresentation = {
  name: string;
  breed: string;
  color: string;
  age: string;
  description: string;
  hasHealthTesting: boolean;
  healthScreening: HealthScreeningItem[];
  gallery: MediaItem[];
};

const calculateAge = (birthDate: string | null): string => {
  if (!birthDate) return '';
  try {
    const birth = new Date(birthDate);
    const now = new Date();
    const years = now.getFullYear() - birth.getFullYear();
    const months = now.getMonth() - birth.getMonth();
    const totalMonths = years * 12 + months;

    if (totalMonths < 12) {
      return `${totalMonths} month${totalMonths !== 1 ? 's' : ''}`;
    }
    const displayYears = Math.floor(totalMonths / 12);
    return `${displayYears} year${displayYears !== 1 ? 's' : ''}`;
  } catch {
    return '';
  }
};

/**
 * Extract health screening data from healthTesting field
 * The field can be an array of {test, date, result} objects or a single object
 */
const extractHealthScreening = (
  healthTesting: unknown
): HealthScreeningItem[] => {
  if (!healthTesting) return [];

  // If it's an array, map each item
  if (Array.isArray(healthTesting)) {
    return healthTesting
      .filter(
        (item): item is { test?: string; date?: string; result?: string } =>
          item !== null && typeof item === 'object'
      )
      .map((item) => ({
        test: item.test || '',
        date: item.date || null,
        result: item.result || null
      }))
      .filter((item) => item.test); // Only include items with a test name
  }

  // If it's a single object (not an array), treat it as a single test result
  if (
    typeof healthTesting === 'object' &&
    healthTesting !== null &&
    !Array.isArray(healthTesting)
  ) {
    const item = healthTesting as {
      test?: string;
      date?: string;
      result?: string;
    };
    // Only return if there's a test name
    if (item.test) {
      return [
        {
          test: item.test || '',
          date: item.date || null,
          result: item.result || null
        }
      ];
    }
  }

  return [];
};

export const presentParentDog = (
  dog: ListingParentDog | null
): ParentDogPresentation | null => {
  if (!dog) return null;

  const healthScreening = extractHealthScreening(dog.healthTesting);

  return {
    name: dog.name || 'Unknown',
    breed: dog.breed?.name || '',
    color: dog.color || '',
    age: calculateAge(dog.birthDate),
    description: dog.description || '',
    hasHealthTesting: healthScreening.length > 0,
    healthScreening,
    gallery: extractMediaFromRaw(dog.gallery)
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Listing summary presentation
// ─────────────────────────────────────────────────────────────────────────────

export type ListingPresentation = {
  title: string;
  breed: string;
  price: string;
  deposit: string;
  availability: string;
  sex: string;
  birthDate: string;
  status: 'available' | 'reserved' | 'rehomed';
  provider: {
    name: string;
    slug: string;
    province: string;
    city: string;
    imageUrl: string | null;
    rating: number | null;
    reviewCount: number;
  } | null;
  media: MediaItem[];
  mother: ParentDogPresentation | null;
  father: ParentDogPresentation | null;
};

export const presentListing = (listing: ListingDetail): ListingPresentation => {
  const status: ListingPresentation['status'] = listing.isRehomed
    ? 'rehomed'
    : listing.isReserved
      ? 'reserved'
      : 'available';

  return {
    title: listing.title || `${listing.breed || 'Puppy'} Listing`,
    breed: listing.breed || '',
    price: formatPrice(listing.price),
    deposit: listing.depositAmount
      ? formatPrice(listing.depositAmount)
      : 'Contact breeder',
    availability: formatAvailability(
      listing.isAvailableNow,
      listing.availableDate
    ),
    sex: formatSex(listing.pup?.sex ?? null),
    birthDate: formatDate(listing.dateOfBirth ?? null),
    status,
    provider: listing.provider
      ? {
          name: listing.provider.name,
          slug: listing.provider.slug,
          province: listing.provider.province || '',
          city: listing.provider.city || '',
          imageUrl: listing.provider.imageUrl,
          rating: listing.provider.averageRating,
          reviewCount: listing.provider.numberOfRatings || 0
        }
      : null,
    media: extractAllMedia(listing),
    mother: presentParentDog(listing.mother),
    father: presentParentDog(listing.father)
  };
};
